// Recording detail API client.
//
// Backend: backend/domain/Models.Read/Recordings/RecordingQueries.cs
//   FindRecordingByIdQuery(Guid Id) : IndexedQueryBase<Recording>
//
// Wire path: POST /api/query  with body { $type: 'FindRecordingByIdQuery', id }
//   → 200 Recording[]  (zero or one element)
//   → 401 / 403        (auth)
//   → 400              (unknown $type — should never happen for generated callers)
//
// The generated dispatcher returns an array because every IndexedQueryBase query
// is implemented as a list-style Marten LINQ projection — we unwrap to a single
// Recording here so the UI keeps its `detail | null` ergonomics and a missing
// recording surfaces as a synthetic 404 ApiResult error.
//
// Re-exports the generated `Recording` read-model shape from
// `@voicedojo/backend-types` so callers don't have to chase the package path.

import { api, ApiError } from './client';
import type { Recording as GeneratedRecording } from '@voicedojo/backend-types/recordings';
import { findRecordingById } from '@voicedojo/backend-types/recordings';

// Re-export the generated QueryDef so callers can use observableQuery
// (wiki/feature-frontend-cqrs-dataflow.md §2a) without chasing the package path.
export { findRecordingById };

export type RecordingDetail = GeneratedRecording;

/**
 * Fetch a single recording by id via `POST /api/query`
 * (`$type: FindRecordingByIdQuery`). The dispatcher returns an array (every
 * `IndexedQueryBase` query is a list projection) — we unwrap to one recording.
 *
 * Resolves the `RecordingDetail` on success; THROWS `ApiError` on a transport
 * failure, and throws a synthetic `ApiError(404)` when the recording is absent
 * (the review page catches both and renders its not-found / error UI).
 */
export async function getRecording(id: string): Promise<RecordingDetail> {
  const recordings = await api.post<RecordingDetail[]>('/query', {
    $type: findRecordingById.$type,
    id,
  });
  const recording = recordings[0];
  if (!recording) {
    throw new ApiError(404, 'Recording not found.');
  }
  return recording;
}
